import {
  BookOpen,
  File,
  FileArchive,
  FileAxis3d,
  FileCode,
  FileImage,
  FileMusic,
  FileSpreadsheet,
  FileText,
  FileVideoCamera,
  Presentation,
  type LucideIcon,
} from 'lucide-react';

type KnowledgeBaseFileIcon = {
  Icon: LucideIcon;
  iconClassName: string;
  containerClassName: string;
};

type FileCategory =
  | 'article'
  | 'word'
  | 'spreadsheet'
  | 'presentation'
  | 'pdf'
  | 'image'
  | 'video'
  | 'audio'
  | 'archive'
  | 'text'
  | 'code'
  | 'cad'
  | 'other';

const WORD_EXTENSIONS = new Set(['doc', 'docx', 'odt', 'rtf']);
const SPREADSHEET_EXTENSIONS = new Set(['xls', 'xlsx', 'csv', 'ods', 'tsv', 'numbers']);
const PRESENTATION_EXTENSIONS = new Set(['ppt', 'pptx', 'odp', 'key']);
const PDF_EXTENSIONS = new Set(['pdf']);
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'tif', 'tiff', 'heic', 'avif']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v', 'wmv']);
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'aac', 'ogg', 'flac', 'm4a']);
const ARCHIVE_EXTENSIONS = new Set(['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz']);
const TEXT_EXTENSIONS = new Set(['txt', 'md', 'rst', 'log']);
const CODE_EXTENSIONS = new Set([
  'json', 'xml', 'html', 'htm', 'css', 'scss', 'less', 'js', 'jsx', 'ts', 'tsx', 'py', 'java',
  'go', 'rs', 'c', 'cc', 'cpp', 'h', 'hpp', 'cs', 'php', 'rb', 'sql', 'sh', 'yaml', 'yml', 'toml', 'ini',
]);
const CAD_EXTENSIONS = new Set(['dwg', 'dxf', 'step', 'stp', 'iges', 'igs', 'skp', 'ai', 'psd', 'fig']);

const WORD_MIME_TYPES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/rtf',
  'application/vnd.oasis.opendocument.text',
]);
const SPREADSHEET_MIME_TYPES = new Set([
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
  'text/csv',
]);
const PRESENTATION_MIME_TYPES = new Set([
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.presentation',
]);
const PDF_MIME_TYPES = new Set(['application/pdf']);
const ARCHIVE_MIME_TYPES = new Set([
  'application/zip',
  'application/x-zip-compressed',
  'application/x-rar-compressed',
  'application/vnd.rar',
  'application/x-7z-compressed',
  'application/gzip',
  'application/x-tar',
  'application/x-bzip2',
]);
const TEXT_MIME_TYPES = new Set(['text/plain', 'text/markdown']);
const CODE_MIME_TYPES = new Set([
  'application/json',
  'application/xml',
  'text/xml',
  'text/html',
  'application/javascript',
  'text/javascript',
  'application/sql',
]);
const CAD_MIME_TYPES = new Set([
  'image/vnd.dwg',
  'application/acad',
  'application/vnd.dxf',
  'application/step',
]);
const URL_WRAPPER_EXTENSIONS = new Set(['aspx', 'asp', 'php', 'jsp', 'html', 'htm']);

function extractExtensionFromText(value?: string): string | null {
  if (!value) return null;
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    decoded = value;
  }
  const normalized = decoded.split('?')[0].split('#')[0];
  const filename = normalized.split('/').pop() || normalized;
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex <= -1 || dotIndex === filename.length - 1) return null;
  return filename.slice(dotIndex + 1).toLowerCase();
}

function extractExtensionFromUrl(webUrl?: string): string | null {
  if (!webUrl) return null;
  try {
    const parsed = new URL(webUrl);
    const queryCandidates = ['file', 'filename', 'name'];
    for (const key of queryCandidates) {
      const queryValue = parsed.searchParams.get(key);
      const extensionFromQuery = extractExtensionFromText(queryValue || undefined);
      if (extensionFromQuery) {
        return extensionFromQuery;
      }
    }
    const extensionFromPath = extractExtensionFromText(parsed.pathname);
    if (extensionFromPath && !URL_WRAPPER_EXTENSIONS.has(extensionFromPath)) {
      return extensionFromPath;
    }
    return null;
  } catch {
    const extension = extractExtensionFromText(webUrl);
    if (extension && !URL_WRAPPER_EXTENSIONS.has(extension)) {
      return extension;
    }
    return null;
  }
}

function normalizeMimeType(mimeType?: string): string | null {
  if (!mimeType) return null;
  return mimeType.split(';')[0].trim().toLowerCase() || null;
}

function resolveExtension(item: { fileExtension?: string | null; title?: string; webUrl?: string }): string | null {
  return item.fileExtension?.toLowerCase() || extractExtensionFromText(item.title) || extractExtensionFromUrl(item.webUrl);
}

function resolveSharePointSharedLinkCategory(webUrl?: string): FileCategory | null {
  if (!webUrl) return null;
  const normalized = webUrl.toLowerCase();
  const match = normalized.match(/\/:([a-z]):\//);
  if (!match) return null;
  const marker = match[1];
  if (marker === 'w') return 'word';
  if (marker === 'x') return 'spreadsheet';
  if (marker === 'p') return 'presentation';
  if (marker === 'b') return 'pdf';
  if (marker === 'i') return 'image';
  if (marker === 'v') return 'video';
  return null;
}

function resolveCategory(item: {
  type: 'article' | 'document';
  title?: string;
  webUrl?: string;
  mimeType?: string | null;
  fileExtension?: string | null;
}): FileCategory {
  if (item.type === 'article') return 'article';

  const mimeType = normalizeMimeType(item.mimeType);
  if (mimeType) {
    if (WORD_MIME_TYPES.has(mimeType)) return 'word';
    if (SPREADSHEET_MIME_TYPES.has(mimeType)) return 'spreadsheet';
    if (PRESENTATION_MIME_TYPES.has(mimeType)) return 'presentation';
    if (PDF_MIME_TYPES.has(mimeType)) return 'pdf';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (ARCHIVE_MIME_TYPES.has(mimeType)) return 'archive';
    if (TEXT_MIME_TYPES.has(mimeType)) return 'text';
    if (CODE_MIME_TYPES.has(mimeType)) return 'code';
    if (CAD_MIME_TYPES.has(mimeType)) return 'cad';
  }

  const extension = resolveExtension(item);
  if (!extension) return 'other';
  if (WORD_EXTENSIONS.has(extension)) return 'word';
  if (SPREADSHEET_EXTENSIONS.has(extension)) return 'spreadsheet';
  if (PRESENTATION_EXTENSIONS.has(extension)) return 'presentation';
  if (PDF_EXTENSIONS.has(extension)) return 'pdf';
  if (IMAGE_EXTENSIONS.has(extension)) return 'image';
  if (VIDEO_EXTENSIONS.has(extension)) return 'video';
  if (AUDIO_EXTENSIONS.has(extension)) return 'audio';
  if (ARCHIVE_EXTENSIONS.has(extension)) return 'archive';
  if (TEXT_EXTENSIONS.has(extension)) return 'text';
  if (CODE_EXTENSIONS.has(extension)) return 'code';
  if (CAD_EXTENSIONS.has(extension)) return 'cad';

  const sharePointCategory = resolveSharePointSharedLinkCategory(item.webUrl);
  if (sharePointCategory) return sharePointCategory;

  return 'other';
}

export function getKnowledgeBaseResultIcon(item: {
  type: 'article' | 'document';
  title?: string;
  webUrl?: string;
  mimeType?: string | null;
  fileExtension?: string | null;
}): KnowledgeBaseFileIcon {
  const category = resolveCategory(item);
  const iconMap: Record<FileCategory, KnowledgeBaseFileIcon> = {
    article: { Icon: BookOpen, iconClassName: 'text-purple-500', containerClassName: 'bg-purple-50' },
    word: { Icon: FileText, iconClassName: 'text-blue-500', containerClassName: 'bg-blue-50' },
    spreadsheet: { Icon: FileSpreadsheet, iconClassName: 'text-green-500', containerClassName: 'bg-green-50' },
    presentation: { Icon: Presentation, iconClassName: 'text-orange-500', containerClassName: 'bg-orange-50' },
    pdf: { Icon: FileText, iconClassName: 'text-red-500', containerClassName: 'bg-red-50' },
    image: { Icon: FileImage, iconClassName: 'text-cyan-500', containerClassName: 'bg-cyan-50' },
    video: { Icon: FileVideoCamera, iconClassName: 'text-violet-500', containerClassName: 'bg-violet-50' },
    audio: { Icon: FileMusic, iconClassName: 'text-pink-500', containerClassName: 'bg-pink-50' },
    archive: { Icon: FileArchive, iconClassName: 'text-amber-600', containerClassName: 'bg-amber-50' },
    text: { Icon: FileText, iconClassName: 'text-slate-500', containerClassName: 'bg-slate-100' },
    code: { Icon: FileCode, iconClassName: 'text-indigo-500', containerClassName: 'bg-indigo-50' },
    cad: { Icon: FileAxis3d, iconClassName: 'text-teal-500', containerClassName: 'bg-teal-50' },
    other: { Icon: File, iconClassName: 'text-gray-500', containerClassName: 'bg-gray-100' },
  };
  return iconMap[category];
}
